/**
 * Content Domain — Barrel Export
 *
 * Public API for the content domain: taxonomy, modules, tiers,
 * questions (type registry, validators, manifest), types.
 *
 * Created in R24 (DDD Fase 3 — content domain consolidation).
 * Updated in R25: questions/ subdomain added.
 * Updated in R30: content types added (ModuleId, QuestionType, QuestionCore, etc.).
 */

// Content Types (canonical source — extracted from src/types.ts in R30)
export type { ModuleId, ModuleData, QuestionType, ClockMode, ClockConfig, ClockFeedback, MoneyMode, MoneyDenom, MoneyConstraints, MoneyConfig, MoneyFeedback, ScheduleEvent, ScheduleConfig, CellRef, MatchingPair, ChartDataPoint, PieSlice, QuestionData, QuestionCore, QuestionFeedback, Question } from './types';

// Modules
export { MODULES } from './modules';
export { getModuleShortName } from './modules';
export { MODULE_ID_TO_CODE, MODULE_CODE_TO_ID } from './modules';

// Module Content (display info, coming soon)
export type { ModuleDisplayInfo } from './moduleContent';
export { getModuleDisplayInfo, COMING_SOON_MODULES, isComingSoon } from './moduleContent';

// Taxonomy (families, skills, lookup helpers)
export {
// Types
type FamilyDefinition, type TierConfig,
// Tier system
TIERS, tierToNiveauCode, niveauCodeToTier, isTierNiveauSynced, isValidTier, isValidNiveauCode, getTierConfig, getTiersForGroup, getTierLabel, getTierTitle, getAllTiers, MAX_TIER, MIN_TIER, getAllowedTiersForGroup, isTierAllowedForGroup, clampTierToGroup, getGroupForTier, getTierRangeForGroup,
// Taxonomy data
TAXONOMY, FAMILY_BY_ID, FAMILY_BY_SUBSKILL, FAMILIES_BY_MODULE, FAMILIES_BY_MODULE_SKILL,
// Per-module data
GET_FAMILIES, REK_FAMILIES, MET_FAMILIES, TIJD_FAMILIES, GELD_FAMILIES, SWW_FAMILIES, SPR_FAMILIES, TAA_FAMILIES, TOPO_FAMILIES, STU_FAMILIES,
// Lookup helpers
isValidFamilyId, getFamilyDefinition, getLearningGoal, getFamiliesForModule, getTierForFamily, isValidSubSkill, getFamilyBySubSkill, validateFamilyConsistency, getAuthoritativeTier, getAuthoritativeLevelCode, getSkillsForModule, getModuleScopedSkillKey, getFamiliesForModuleSkill, getAllModuleScopedSkills, isValidModuleSkill, getSkillStatsForModule, validateSkillConsistency, getLargestSkills, getTaxonomyStats } from './taxonomy';

// Module Colors (display utilities)
export type { ModuleColorSet } from './moduleColors';
export { getModuleColors, getModuleHexColor, MODULE_COLOR_MAP } from './moduleColors';

// Module Intro ("Wat leer je hier?" registry)
export type { ModuleIntroData } from './moduleIntro';
export { getModuleIntro } from './moduleIntro';

// Icon Resolver (type-safe icon lookup)
export { getModuleIcon } from './iconResolver';

// Questions — Type Registry
export { QUESTION_TYPE_REGISTRY, getQuestionTypeConfig } from './questions/questionTypeRegistry';

// Questions — Validator Registry
export type { ValidatorFn } from './questions/validatorRegistry';
export { VALIDATOR_REGISTRY, getValidator, validateAnswer } from './questions/validatorRegistry';

// Questions — Seed Consolidator
export type { SeedQuestionSourceMetadata, SeedQuestion, SeedCollection, ConsolidationReport } from './questions/seedConsolidator';
export { MODULE_CODES, SEED_COLLECTIONS, initializeSeedCollections, isSeedSystemReady, validateSeedCoverage, validateSeedQuestionsLocal, getSeedQuestionTypeDistribution, validateCrossSeedDuplicateIds, getConsolidationReport, getSeedCollection, getAllSeedQuestions, getModuleSeedQuestions, getSeedQuestionsByTier, getSeedQuestionsByFamily, validateSeedQuestionsGovernanceReport } from './questions/seedConsolidator';

// Questions — Question Loader (core: indexes, retrieval, stats)
export { ALL_QUESTIONS_CORE, QUESTION_BY_ID, QUESTIONS_BY_MODULE, QUESTIONS_BY_MODULE_SUBSKILL, QUESTIONS_BY_FAMILY, SUBSKILLS_BY_MODULE, QUESTIONS_BY_MODULE_TIER, CITO_ELIGIBLE_QUESTIONS, getCitoEligibleQuestions, getModuleQuestions, getModuleTierQuestions, getModuleSubSkillQuestions, getModuleSubSkillTierQuestions, getQuestionWithExplanation, getQuestionsByModule, getAllQuestions, getQuestionsForTier, getQuestionsForFamily, getQuestionsForFamilyInTierRange, getSeedStats, initializeQuestionLoader } from './questions/questionLoader';

// ── Content System Initialization ────────────────────────────────────────────
// Single entry point for async content loading (Bundle Optimization Phase 2).
// Call this once at app startup before accessing any question data.
export { initializeContentSystem, ensureAllContentLoaded, ensureModuleLoaded, isContentLoaded, isModuleContentLoaded, prefetchAllContentWhenIdle, getContentLoadStatus } from './questions/contentInit';

// Questions — Validation (pure validation, no circular deps)
export { assertCanonicalSeedFeedback, isCitoEligible, validateQuestion } from './questions/questionValidation';

// Questions — Data Schemas (PEDAGOGICAL_CONTRACT_SPEC §3.2)
export { validateQuestionData, hasDataSchema } from './questions/dataSchemas';
export type { ValidationResult as DataValidationResult } from './questions/dataSchemas';

// Questions — Shuffling (option shuffling utilities)
export { fisherYatesShuffle, shuffleQuestionOptions, shuffleQuestionWithExplanations } from './questions/questionShuffling';

// NOTE: citoSelection.ts was archived in v4.0 audit — orphaned module not used by assessment system.
// See docs/audits/SYSTEM_LOGIC_AUDIT_2026.md §4.4 for details.

// Family Labels (human-readable display names for familyIds)
export { FAMILY_DISPLAY_NAMES, humanizeFamilyId } from './familyLabels';